import { useEffect } from 'react';
import { useAuth } from 'contexts/AuthContext';
import { updateUserData } from 'lib/supabase-db';

// Local storage key for signup extra details
const SIGNUP_DETAILS_KEY = 'vibe_signup_extra_details';

interface SignupDetails {
  firstName: string;
  lastName: string;
  companyName: string;
}

export const useSignupExtraDetails = () => {
  const { user } = useAuth();

  useEffect(() => {
    const processSignupDetails = async () => {
      // Skip if user is not logged in
      if (!user) {
        return;
      }

      // Check if we have signup details in localStorage
      const storedDetails = localStorage.getItem(SIGNUP_DETAILS_KEY);
      if (!storedDetails) {
        return;
      }

      let signupDetails: SignupDetails;
      try {
        signupDetails = JSON.parse(storedDetails);
      } catch (error) {
        console.error('Error parsing signup details from localStorage:', error);
        localStorage.removeItem(SIGNUP_DETAILS_KEY);
        return;
      }

      // Check if user already has this data in metadata
      const userMetadata = user.user_metadata || {};
      const hasFirstName = !!userMetadata.first_name;
      const hasLastName = !!userMetadata.last_name;
      const hasCompanyName = !!userMetadata.company_name;

      // If user already has all the data, clean up localStorage and exit
      if (hasFirstName && hasLastName && hasCompanyName) {
        localStorage.removeItem(SIGNUP_DETAILS_KEY);
        return;
      }

      // Update user with missing data from localStorage
      try {
        const updateData: Partial<SignupDetails> = {};

        if (!hasFirstName && signupDetails.firstName) {
          updateData.firstName = signupDetails.firstName;
        }
        if (!hasLastName && signupDetails.lastName) {
          updateData.lastName = signupDetails.lastName;
        }
        if (!hasCompanyName && signupDetails.companyName) {
          updateData.companyName = signupDetails.companyName;
        }

        // Only update if there's actually data to update
        if (Object.keys(updateData).length > 0) {
          await updateUserData(updateData);
          console.log('Updated user metadata with signup details:', updateData);
        }

        // Clean up localStorage after successful update
        localStorage.removeItem(SIGNUP_DETAILS_KEY);
      } catch (error) {
        console.error('Error updating user metadata with signup details:', error);
        // Don't remove from localStorage on error, so we can retry later
      }
    };

    processSignupDetails();
  }, [user]);
};

// Utility function to save signup details to localStorage
export const saveSignupDetails = (details: SignupDetails) => {
  localStorage.setItem(SIGNUP_DETAILS_KEY, JSON.stringify(details));
};

// Utility function to clear signup details from localStorage
export const clearSignupDetails = () => {
  localStorage.removeItem(SIGNUP_DETAILS_KEY);
};